import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ShiftFilters = ({ filters, onFiltersChange, onSaveFilter, savedFilters, onLoadFilter }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  const departmentOptions = [
    { value: 'customer-service', label: 'Kundendienst' },
    { value: 'operations', label: 'Betrieb' },
    { value: 'manufacturing', label: 'Fertigung' },
    { value: 'administration', label: 'Verwaltung' },
    { value: 'maintenance', label: 'Wartung' },
    { value: 'security', label: 'Sicherheit' }
  ];

  const shiftTypeOptions = [
    { value: 'morning', label: 'Frühschicht' },
    { value: 'afternoon', label: 'Spätschicht' },
    { value: 'night', label: 'Nachtschicht' },
    { value: 'weekend', label: 'Wochenende' },
    { value: 'overtime', label: 'Überstunden' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleShiftTypeChange = (type, checked) => {
    const updatedTypes = checked 
      ? [...filters?.shiftTypes, type]
      : filters?.shiftTypes?.filter(t => t !== type);
    
    handleFilterChange('shiftTypes', updatedTypes);
  };

  const handleSaveFilter = () => {
    if (filterName?.trim()) {
      onSaveFilter(filterName, filters);
      setFilterName('');
      setShowSaveDialog(false);
    }
  };

  const clearAllFilters = () => {
    onFiltersChange({
      dateFrom: '',
      dateTo: '',
      departments: [],
      shiftTypes: [],
      minHours: 0,
      maxHours: 12,
      searchQuery: '',
      showOnlyPriority: false,
      showCrossTraining: false
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Filter & Suche</h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            iconName={showAdvanced ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            Erweitert
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllFilters}
            iconName="X"
            iconPosition="left"
          >
            Zurücksetzen
          </Button>
        </div>
      </div>
      {/* Basic Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Input
          label="Von Datum"
          type="date"
          value={filters?.dateFrom}
          onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
        />
        
        <Input
          label="Bis Datum"
          type="date"
          value={filters?.dateTo}
          onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
        />

        <Select
          label="Abteilungen"
          multiple
          searchable
          options={departmentOptions}
          value={filters?.departments}
          onChange={(value) => handleFilterChange('departments', value)}
          placeholder="Abteilung wählen"
        />

        <Input
          label="Suche"
          type="search"
          placeholder="z.B. 'Morgenschichten nächste Woche'"
          value={filters?.searchQuery}
          onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
        />
      </div>
      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Shift Types */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Schichtarten
              </label>
              <div className="space-y-2">
                {shiftTypeOptions?.map((type) => (
                  <Checkbox
                    key={type?.value}
                    label={type?.label}
                    checked={filters?.shiftTypes?.includes(type?.value)}
                    onChange={(e) => handleShiftTypeChange(type?.value, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>

            {/* Hours Range */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Stunden Bereich
              </label>
              <div className="space-y-3">
                <Input
                  label="Min. Stunden"
                  type="number"
                  min="0"
                  max="12"
                  value={filters?.minHours}
                  onChange={(e) => handleFilterChange('minHours', parseInt(e?.target?.value) || 0)}
                />
                <Input
                  label="Max. Stunden"
                  type="number"
                  min="0"
                  max="12"
                  value={filters?.maxHours}
                  onChange={(e) => handleFilterChange('maxHours', parseInt(e?.target?.value) || 12)}
                />
              </div>
            </div>

            {/* Special Options */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Spezielle Optionen
              </label>
              <div className="space-y-2">
                <Checkbox
                  label="Nur Prioritäts-Schichten"
                  description="Schichten mit höherer Priorität für Senioren"
                  checked={filters?.showOnlyPriority}
                  onChange={(e) => handleFilterChange('showOnlyPriority', e?.target?.checked)}
                />
                <Checkbox
                  label="Weiterbildungsmöglichkeiten"
                  description="Schichten mit Lernmöglichkeiten"
                  checked={filters?.showCrossTraining}
                  onChange={(e) => handleFilterChange('showCrossTraining', e?.target?.checked)}
                />
              </div>
            </div>
          </div>

          {/* Saved Filters */}
          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-foreground">Gespeicherte Filter</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSaveDialog(!showSaveDialog)}
                iconName="Save"
                iconPosition="left"
              >
                Filter speichern
              </Button>
            </div>

            {showSaveDialog && (
              <div className="flex items-center space-x-2 mb-3">
                <Input
                  placeholder="Filter Name eingeben"
                  value={filterName}
                  onChange={(e) => setFilterName(e?.target?.value)}
                  className="flex-1"
                />
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveFilter}
                  disabled={!filterName?.trim()}
                >
                  Speichern
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSaveDialog(false)}
                  iconName="X"
                />
              </div>
            )}

            {savedFilters?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {savedFilters?.map((savedFilter, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => onLoadFilter(savedFilter)}
                    className="text-xs"
                  >
                    {savedFilter?.name}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftFilters;